import React, { useRef } from "react";
import { Drawer, Card, Typography, Button } from "antd";
import { CloseCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { IReparationCaseDetails } from "api/requests";

const { Text } = Typography;

interface PrintReparationLabelProps {
  open: boolean;
  onClose: () => void;
  reparationData: IReparationCaseDetails | undefined;
}

const PrintReparationLabel: React.FC<PrintReparationLabelProps> = ({
  open,
  onClose,
  reparationData,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const company = useSelector((state: any) => state.company);

  const handlePrint = () => {
    if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open("", "_blank", "width=600,height=600");
      if (printWindow) {
        const printHtml = `
          <html>
            <head>
              <title>Print Reparation Label</title>
              <style>
                /* Set page size for label printing */
                @page {
                  size: 80mm 60mm;
                  margin: 0;
                }
                @media print {
                  html, body {
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    height: 100%;
                    transform: scale(0.99);
                  }
                  .printable-label {
                    width: 80mm !important;
                    height: 60mm !important;
                    margin: 0 !important;
                    padding: 8px !important;
                    box-sizing: border-box;
                    overflow: hidden;
                    page-break-after: always;
                    page-break-inside: avoid;
                    font-family: Arial, sans-serif;
                  }
                  /* Override Ant Design Card styles for printing */
                  .ant-card {
                    margin: 0 !important;
                    padding: 0 !important;
                    border: none !important;
                    box-shadow: none !important;
                  }
                }
              </style>
            </head>
            <body>
              ${printContents}
            </body>
          </html>
        `;
        printWindow.document.open();
        (printWindow.document.write as (s: string) => void)(printHtml);
        printWindow.document.close();

        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    }
  };

  const getProductName = () => {
    // Get product/service names from reparation items following actual response structure
    //@ts-ignore
    const items = reparationData?.reparationItems
      ?.map((item: any) => {
        if (item.product) {
          return (
            item.product.productName +
            (item.product.productGrade
              ? ` (${item.product.productGrade})`
              : "") +
            (item?.product?.productVariantValues?.find(
              (v: any) =>
                v?.productVariantValue?.productVariantType?.name ===
                "Frame Compatibility"
            )
              ? ` Frame: ${
                  item?.product?.productVariantValues?.find(
                    (v: any) =>
                      v?.productVariantValue.productVariantType?.name ===
                      "Frame Compatibility"
                  )?.productVariantValue?.name
                }`
              : "")
          );
        }
        if (item.service) {
          return item.service.serviceName;
        }
        return item.description || "Item";
      })
      .filter(Boolean);
    return items && items.length > 0 ? items.join(", ") : "-";
  };

  const getProductGrade = () => {
    // Extract product grade from reparation items if available, only for actual products (not services)
    //@ts-ignore
    const productGrades = reparationData?.reparationItems
      ?.map((item: any) => {
        // Only show product grade if it's an actual product (has product field) and has a grade
        if (item.product && item.product.productGrade) {
          return item.product.productGrade;
        }
        return null;
      })
      .filter(Boolean);
    return productGrades && productGrades.length > 0
      ? productGrades.join(", ")
      : "-";
  };

  const getReparationManName = () => {
    // Find the "Repair Started" history entry to get the repair man name
    //@ts-ignore
    const repairStartedEntry = reparationData?.reparationCaseHistoryItems?.find(
      (item: any) =>
        item.action === "REPAIRING" && item.title === "Repair Started"
    );
    //@ts-ignore
    return repairStartedEntry?.user?.userFullName || "-";
  };

  const getPaymentMethod = () => {
    // Get payment method from reparation case following actual response structure
    //@ts-ignore
    if (reparationData?.paymentMethod) {
      //@ts-ignore
      return typeof reparationData.paymentMethod === "object"
        ? //@ts-ignore
          reparationData.paymentMethod.paymentMethodName
        : //@ts-ignore
          reparationData.paymentMethod;
    }
    return "-";
  };

  const getTotalAmount = () => {
    // Calculate total from reparation items following actual response structure
    const total =
      //@ts-ignore
      reparationData?.reparationItems?.reduce((sum: number, item: any) => {
        return sum + (item.totalPrice || 0);
      }, 0) || 0;
    return total;
  };

  const renderReparationLabel = () => {
    if (!reparationData) return null;

    const productGrade = getProductGrade();
    const hasProductGrade = productGrade !== "-";

    return (
      <Card
        className="printable-label"
        style={{
          width: "100%",
          maxWidth: "80mm",
          margin: "10px auto",
          border: "1px solid #d9d9d9",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "58mm",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            fontSize: "12px",
            lineHeight: "1.2",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <Text strong style={{ fontSize: "14px" }}>
              REPARATION LABEL
            </Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Case Number:</Text>
            {/* @ts-ignore */}
            <Text>{reparationData.idReparationCase}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Issue Date:</Text>
            <Text>
              {/* @ts-ignore */}
              {dayjs(reparationData.createdDate).format("DD/MM/YYYY")}
            </Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Customer Name:</Text>
            {/* @ts-ignore */}
            <Text>{`${reparationData.firstName} ${reparationData.lastName}`}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Customer Phone:</Text>
            {/* @ts-ignore */}
            <Text>{reparationData.primaryPhone || "-"}</Text>
          </div>


          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>IMEI:</Text>
            {/* @ts-ignore */}
            <Text>{reparationData.imeiNumber || "-"}</Text>
          </div>


          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Model:</Text>
            {/* @ts-ignore */}
            <Text>{reparationData.model || "-"}</Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Text strong>Device Password:</Text>
            {/* @ts-ignore */}
            <Text>
              {(reparationData.deviceLockCode || reparationData.devicePattern || "-")}
              {" "}
              <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>
                [
                {reparationData.deviceLockCode ? "Code" : reparationData.devicePattern ? "Pattern" : "-"}
                ]
              </span>
            </Text>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>Product Name:</Text>
            <Text
              style={{ fontSize: "10px", textAlign: "right", maxWidth: "50%" }}
            >
              {getProductName()}
            </Text>
          </div>


          {hasProductGrade && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text strong>Product Grade:</Text>
              <Text>{productGrade}</Text>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "8px",
              paddingTop: "4px",
              borderTop: "1px solid #d9d9d9",
            }}
          >
            <Text strong style={{ fontSize: "13px" }}>
              Total Amount:
            </Text>
            <Text strong style={{ fontSize: "13px" }}>
              {getTotalAmount()} {company.currencySymbol}
            </Text>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Drawer
      title="Print Reparation Label"
      placement="bottom"
      height="100%"
      onClose={onClose}
      open={open}
    >
      <div style={{ marginBottom: 20 }}>
        <Text>Preview of the reparation label that will be printed:</Text>
      </div>

      <div
        ref={printRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {renderReparationLabel()}
      </div>

      <div
        style={{
          position: "fixed",
          bottom: "15px",
          left: "40%",
          background: "white",
          zIndex: 5,
          padding: "10px 20px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "10px",
        }}
      >
        <Button
          icon={<CloseCircleOutlined />}
          style={{ margin: "0 10px" }}
          onClick={onClose}
        />
        <Button
          icon={<PrinterOutlined />}
          style={{ margin: "0 10px" }}
          onClick={handlePrint}
          type="primary"
        />
      </div>
    </Drawer>
  );
};

export default PrintReparationLabel;
